import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles/index';
import {Button} from "@material-ui/core";
import {Row, Col} from 'react-flexa';
import Image from "../../../../components/Image";
import isEmpty from "lodash/isEmpty";
import {
  checkCreditCardType,
  checkCreditCardExpiration,
  validateRequiredFields,
  checkCreditCard
} from '../../../../helpers';
import asFormControl from '../../../../components/FormControl';
import Typography from "../../../../components/Typography";
import InputAdornment from '@material-ui/core/InputAdornment';
import isNull from "lodash/isNull";
import CreditCardIcon from '@material-ui/icons/CreditCard';


const styles = theme => ({
  form: {
    maxWidth: 500,
    margin: '0 auto',
  },
  inputField: {
    backgroundColor: theme.palette.secondary.light
  },
  input: {
    color: 'rgb(27, 101, 166)',
    fontWeight: 'bold'
  },
  button: {
    fontWeight: '900',
    boxShadow: '0 30px 30px -10px rgba(0,0,0,0.3)',
    padding: '1rem 0',
    margin: '2px 0 12px 0',
    letterSpacing: '2px',
    lineHeight: '28px',
    [theme.breakpoints.up('lg')]: {
      maxWidth: '75%'
    }
  },
  buttonLabel: {
    fontWeight: 'bold',
  },
  cardInactive: {
    filter: 'grayscale(100%)'
  },
});

// fields are named as such to match global CC validation helpers
const baseRequiredFields = ['CardNumber', 'Expiration', 'CVV'];

class CreditCardForm extends Component {
  constructor(props) {
    super(props);
    this.FormComponent = asFormControl(this, baseRequiredFields, false);
    this.state = {
      errors: [],
      openErrorDialog: false,
      formData: {
        CardType: null,
        CardNumber: '',
        Expiration: '', // used as a placeholder to populate year and month
        ExpirationMonth: '',
        ExpirationYear: '',
        CVV: '',
      },
      formErrors: {},
      showErrors: false,
      selectedCard: null,
    };

    if (props.triggerSubmit) props.triggerSubmit(this.handleSubmit);
  }

  handleCardRender = (type) => {
    const {selectedCard} = this.state;
    return selectedCard === type || isNull(selectedCard) ? '' : this.props.classes.cardInactive
  };

  getCurrentDate() {
    const today = new Date();
    return {month: today.getMonth() + 1, year: `${today.getFullYear()}`.slice(2, 4)};
  };

  getMonthYearFromExpiration(Expiration) {
    try {
      return {month: Expiration.slice(0, 2), year: `20${Expiration.slice(2, 4)}`}
    } catch (err) {
      return this.getCurrentDate();
    }
  };

  // called from the this.FormComponent object.
  updateFormData = name => (e, value = null) => {
    const {formData} = this.state;
    formData[name] = value !== null ? value : e.target.value;
    if (name === 'Expiration') {
      const {month, year} = this.getMonthYearFromExpiration(formData[name]);
      formData['ExpirationMonth'] = month;
      formData['ExpirationYear'] = year;
    }
    this.setState({formData})
  };

  checkCreditCardType = () => {
    let {formData} = this.state;
    const {CardNumber} = formData;
    const CardType = checkCreditCardType(CardNumber);
    formData.CardType = CardType;
    this.setState({formData}, () => this.handleCardTypeDisplay(CardType));
  };

  handleCardTypeDisplay = (selectedCard) => {
    this.setState({selectedCard})
  };

  validateCreditCardLength = () => {
    const {CardNumber, CardType} = this.state.formData;
    let formErrors = {};
    if (CardType) {
      const cardValidator = CardType === '4' ? /^\d{15}$/ : /^\d{16}$/;
      if (!cardValidator.test(CardNumber)) {
        formErrors.CardNumber = 'Invalid Card Number Length';
      }
      return formErrors;
    }
    // if CardType is missing, the checkCreditCard function would raise the error.
    return formErrors;
  };

  validateFormData = () => {
    const {formData} = this.state;
    let formErrors = {};
    formErrors = validateRequiredFields(baseRequiredFields, formData, formErrors);
    // If fields are missing then don't bother validating further.
    if (!isEmpty(formErrors)) {
      return formErrors
    }
    const {CardNumber, ExpirationMonth, ExpirationYear, CardType, CVV} = formData;
    const ccValidatorMsg = checkCreditCard(CardNumber, CardType, CVV, ExpirationMonth, ExpirationYear, true);
    const ccLengthMsg = this.validateCreditCardLength();
    formErrors = {...formErrors, ...ccValidatorMsg, ...ccLengthMsg};
    if (!checkCreditCardExpiration(ExpirationMonth, ExpirationYear)) {
      formErrors.Expiration = 'Invalid Expiration Date';
    }
    return formErrors;
  };

  handleSubmit = e => {
    if (e) e.preventDefault();
    const formErrors = this.validateFormData();
    const isValid = isEmpty(formErrors);
    if (!isValid) {
      this.setState({formErrors, showErrors: true});
    } else {
      const {formData} = this.state;
      if (this.props.onSubmit) this.props.onSubmit(isValid, formData);
      this.setState({formErrors: {}, showErrors: false});
    }
  };

  renderErrors(formErrors) {
    return Object.keys(formErrors).map((key, index) => (
      <Typography key={index} variant='subtitle2' color='primary' bold={true}>{key}: {formErrors[key]}</Typography>))
  }

  render() {
    const {classes} = this.props;
    const {formErrors, errors, showErrors, formData, selectedCard} = this.state;
    const {CardType} = formData;
    const FormComponent = this.FormComponent;
    const FormComponentProps = {
      className: classes.inputField,
      placeholderLabel: true,
      InputProps: {
        className: classes.input
      },
      showErrors
    };
    const cvvLength = CardType === '4' ? 4 : 3;
    return (
      <React.Fragment>
        {!isEmpty(formErrors) && (<div>
          <Typography variant='subtitle2' color='primary' bold={true}>Please correct the errors and submit
            again.</Typography>
          {this.renderErrors(formErrors)}
        </div>)}
        {!isEmpty(errors) && (<div>
          {errors.map((error, index) => (
            <Typography key={index} variant='subtitle2' color='primary' bold={true}>{error}</Typography>
          ))}
        </div>)}
        <form action='' method='post'>
          <Row>
            <Col xs={12}>
              <FormComponent name='CardNumber' label={null}
                             mask={[/\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/]}
                             inputProps={{
                               minLength: 15,
                               maxLength: 16,
                               placeholder: 'Card Number',
                               onBlur: this.checkCreditCardType
                             }} {...FormComponentProps}
                             InputProps={{
                               ...FormComponentProps.InputProps,
                               startAdornment: (
                                 <InputAdornment position="start">
                                   { !selectedCard && <CreditCardIcon /> }
                                   { selectedCard === '1' && <Image src='/visa.svg'/> }
                                   { selectedCard === '2' && <Image src='/mastercard.svg'/> }
                                   { selectedCard === '4' && <Image src='/amex.svg'/> }
                                   { selectedCard === '3' && <Image src='/discover.svg'/> }
                                 </InputAdornment>
                               )
                             }}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={8}>
              <FormComponent name='Expiration' label={null}
                             mask={[/\d/, /\d/, '/', /\d/, /\d/]}
                             inputProps={{
                               minLength: 4,
                               maxLength: 4,
                               placeholder: 'MM/YY',
                             }}
                             {...FormComponentProps}/>
            </Col>
            <Col xs={4}>
              <FormComponent name='CVV' label={null}
                             inputProps={{
                               minLength: cvvLength,
                               maxLength: cvvLength,
                               placeholder: 'CVC'
                             }} {...FormComponentProps}/>
            </Col>
          </Row>
        </form>
      </React.Fragment>
    );
  }
}

CreditCardForm.propTypes = {
  classes: PropTypes.object.isRequired,
  ftniSessionId: PropTypes.string,
  triggerSubmit: PropTypes.func.isRequired,
};

export default withStyles(styles)(CreditCardForm);
